import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useTheme } from '../../contexts/ThemeContext';
import NavigationBar from '../NavigationBar';
import PredictionsSidebar from './PredictionsSidebar';
import PredictionView from './PredictionView';
import { PanelLeft, Trophy, Loader2 } from 'lucide-react';

export default function SharedPredictionView() {
  const { shareId } = useParams();
  const { darkMode } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 768);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!shareId) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'shared_predictions', shareId));
        if (!snap.exists()) {
          setError('Prediction not found');
        } else {
          setData(snap.data());
        }
      } catch (err) {
        console.error('Failed to load shared prediction:', err);
        setError('Failed to load prediction');
      }
      setLoading(false);
    })();
  }, [shareId]);

  // Build sidebar data from the single shared prediction
  const predictionDates = data ? [data.date] : [];
  const predictionsMeta = data ? {
    [data.date]: {
      recommendation: data.recommendation || null,
      tickers: data.tickers || [],
      generatedAt: data.generatedAt,
    }
  } : {};

  return (
    <div className="app-route-canvas min-h-screen">

      <NavigationBar />

      <PredictionsSidebar
        dates={predictionDates}
        selectedDate={data?.date || null}
        onSelectDate={() => {}}
        predictions={predictionsMeta}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen(false)}
        darkMode={darkMode}
      />

      {/* Main content */}
      <div
        className="relative z-10 transition-all duration-300 pt-20 pb-12"
        style={{ marginLeft: sidebarOpen ? '260px' : '0px' }}
      >
        {!sidebarOpen && (
          <div className={`flex items-center gap-3 px-6 py-3 mb-4 border-b ${
            darkMode ? 'border-white/[0.06]' : 'border-gray-100'
          }`}>
            <button
              onClick={() => setSidebarOpen(true)}
              className={`p-2 rounded-lg transition-all duration-200 border-none cursor-pointer ${
                darkMode ? 'text-[#888] hover:text-[#ccc] hover:bg-white/[0.06]' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
            >
              <PanelLeft size={18} />
            </button>
            <span className={`text-[0.75rem] font-medium flex items-center gap-1.5 ${darkMode ? 'text-[#555]' : 'text-[#bbb]'}`}>
              <Trophy size={13} />
              Shared Prediction
            </span>
          </div>
        )}

        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          {loading && (
            <div className="flex items-center justify-center py-24">
              <Loader2 className={`w-6 h-6 animate-spin ${darkMode ? 'text-white/40' : 'text-gray-400'}`} />
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center py-24">
              <Trophy size={40} className={`mb-4 ${darkMode ? 'text-white/15' : 'text-gray-200'}`} />
              <h2 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white/70' : 'text-gray-700'}`}>
                {error}
              </h2>
              <p className={`text-sm ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>
                This prediction may have been removed or the link may be incorrect.
              </p>
            </div>
          )}

          {!loading && !error && data && (
            <PredictionView
              date={data.date}
              data={data}
              darkMode={darkMode}
              loading={false}
              readOnly={true}
            />
          )}
        </div>
      </div>
    </div>
  );
}
